import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react';
import { TYPES } from './iocTypes';
import { Tools } from './ToolStateStore';

import { ReactComponent as DeleteIcon } from '@/assets/icons/tools/delete.svg';
import { ReactComponent as AngleIcon } from '@/assets/icons/tools/angle.svg';
import { ReactComponent as DistanceIcon } from '@/assets/icons/tools/distance.svg';
import { ReactComponent as AnnotationIcon } from '@/assets/icons/tools/anno.svg';
import { ReactComponent as AreaIcon } from '@/assets/icons/tools/area.svg';
import { ReactComponent as SplineIcon } from '@/assets/icons/tools/spline.svg';

import ButtonGroup from '@mui/material/ButtonGroup';
import { SvgIconButton } from '@/components/common/SvgIconButton';
import { Divider, Tooltip } from '@mui/material';

import type { ToolSelectorController } from './ToolSelector.controller';
import type { IToolBoxPanelControls } from '@/engine/app/modules/indications/interfaces.ts';
import type { FC } from 'react';
import styled from 'styled-components';
import { Fragment } from 'react';
import { useOptionalSurgerySpecificUiConfigValue } from '../ui/useUiConfigValue';

interface Props {
  orientation: 'vertical' | 'horizontal';
}

export const ToolSelector: FC<Props> = observer(({ orientation }) => {
  const toolSelectorController: ToolSelectorController = useInjection<ToolSelectorController>(
    TYPES.ToolSelectorController
  );
  const omittedIndicationTools = useOptionalSurgerySpecificUiConfigValue<Array<Tools>>('omittedIndicationTools');

  const toolsData = (
    [
      { name: 'annotation', label: 'Add annotation', tool: Tools.Annotation, icon: AnnotationIcon },
      { name: 'area', label: 'Add area', tool: Tools.Area, icon: AreaIcon },
      { name: 'spline', label: 'Add spline', tool: Tools.Spline, icon: SplineIcon },
      { name: 'distance', label: 'Add distance', tool: Tools.Distance, icon: DistanceIcon },
      { name: 'angle', label: 'Add angle', tool: Tools.Angle, icon: AngleIcon },
      { name: 'delete', label: 'Delete', tool: Tools.Delete, icon: DeleteIcon }
    ] as Array<IToolBoxPanelControls>
  ).filter(({ tool }) => !omittedIndicationTools?.includes(tool));

  return (
    <IndicationButtonsGroup variant="text" orientation={orientation} data-testid={'tool-selector-container'}>
      {toolsData.map((control: IToolBoxPanelControls, i: number) => {
        return (
          <Fragment key={i}>
            {control.name === 'delete' && <Divider orientation="vertical" flexItem />}
            <Tooltip title={control.label} placement={'top'} arrow={true}>
              <SvgIconButton
                data-testid={`tool-selector-${control.name}`}
                $isActive={toolSelectorController.isSelected(control.tool)}
                aria-label={control.name}
                onClick={(): void => toolSelectorController.setCurrentTool(control.tool)}
                component={control.icon}
              />
            </Tooltip>
          </Fragment>
        );
      })}
    </IndicationButtonsGroup>
  );
});

const IndicationButtonsGroup = styled(ButtonGroup)`
  display: flex;
  justify-content: space-evenly;
  margin: 0 2em;
`;
